import { useState, useCallback } from "react";
import { Upload, Film, X, Cloud } from "lucide-react";
import { getAuthToken } from "@/lib/queryClient";
import CloudStoragePicker from "@/components/cloud-storage-picker";

interface VideoUploadProps {
  onUploaded: (fileId: string, fileName: string) => void;
  isUploading: boolean;
  setIsUploading: (v: boolean) => void;
}

const ALLOWED_EXTENSIONS = [".mp4", ".mov", ".avi", ".webm", ".mkv"];
const MAX_SIZE_MB = 500;

export default function VideoUpload({ onUploaded, isUploading, setIsUploading }: VideoUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);

  const validateFile = (file: File): string | null => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Niedozwolony format. Dozwolone: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `Plik za duży. Maksymalnie ${MAX_SIZE_MB}MB`;
    }
    return null;
  };

  const uploadFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("video", file);

        const headers: Record<string, string> = {};
        const token = getAuthToken();
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch("/api/video-editor/upload", {
          method: "POST",
          headers,
          body: formData,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Upload failed");
        }

        const data = await res.json();
        onUploaded(data.fileId, data.originalName);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Błąd uploadu";
        setError(msg);
      } finally {
        setIsUploading(false);
      }
    },
    [onUploaded, setIsUploading]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [uploadFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadFile(file);
    },
    [uploadFile]
  );

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`
          w-full max-w-lg border-2 border-dashed rounded-xl p-12
          flex flex-col items-center gap-4 cursor-pointer transition-all
          ${
            dragActive
              ? "border-[#ef3e29] bg-[#ef3e29]/10"
              : "border-white/20 hover:border-white/40 bg-white/[0.02]"
          }
          ${isUploading ? "pointer-events-none opacity-60" : ""}
        `}
        onClick={() => document.getElementById("video-file-input")?.click()}
      >
        {isUploading ? (
          <>
            <div className="w-10 h-10 border-3 border-white/20 border-t-[#ef3e29] rounded-full animate-spin" />
            <p className="text-white/60 text-sm">Przesyłanie...</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-white/[0.06] flex items-center justify-center">
              <Upload size={24} className="text-white/40" />
            </div>
            <div className="text-center">
              <p className="text-white/80 text-sm font-medium">Przeciągnij wideo lub kliknij</p>
              <p className="text-white/30 text-xs mt-1">
                MP4, MOV, AVI, WebM, MKV • max {MAX_SIZE_MB}MB
              </p>
            </div>
          </>
        )}
        <input
          id="video-file-input"
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFileInput}
          disabled={isUploading}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 px-4 py-2 rounded-lg">
          <X size={14} />
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => setCloudPickerOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06] text-white/50 hover:text-white/70 text-sm transition-all"
      >
        <Cloud size={16} />
        Importuj z Google Drive
      </button>

      <div className="flex items-center gap-2 text-white/20 text-xs">
        <Film size={12} />
        <span>Upload → auto-napisy AI → styl animacji → gotowa rolka</span>
      </div>

      <CloudStoragePicker
        open={cloudPickerOpen}
        onClose={() => setCloudPickerOpen(false)}
        onFileImported={(file) => onUploaded(file.fileId, file.originalName)}
        accept="video"
      />
    </div>
  );
}
